import { createWidgetRegistry, type WidgetModule } from "@salestrak/ui";
// One shared definitions home: one kit, one registry, one `definitions/` folder
// — so every feature mounted under the app-level
// `<WidgetRegistryProvider registry={widgetRegistry}>` (the dashboard grid, the
// Add-Card gallery, settings surfaces) resolves EVERY widget type with no second
// registry to shadow it. The authoring catalog is curated purely by the type set
// below — the registry itself never separates resolution from authoring.
// Definitions import domain contexts via direct module paths, never a feature
// barrel — the barrels are what import this registry, so barrel imports from
// inside a definition would create cycles.
import { sectionHeaderDef } from "#app/widgets/definitions/section-header";
import { metricDef } from "#app/widgets/definitions/metric";
import { dimensionBarDef } from "#app/widgets/definitions/dimension-bar";
import { dimensionBatteryDef } from "#app/widgets/definitions/dimension-battery";
import { typeMixDef } from "#app/widgets/definitions/type-mix";
import { seriesLineDef } from "#app/widgets/definitions/series-line";
import { stageAgeDef } from "#app/widgets/definitions/stage-age";
import { stageMovesDef } from "#app/widgets/definitions/stage-moves";
import { projectsClosedDef } from "#app/widgets/definitions/projects-closed";
import { mostActiveUsersDef } from "#app/widgets/definitions/most-active-users";
import { entityListDef } from "#app/widgets/definitions/entity-list";
import { favoritesListDef } from "#app/widgets/definitions/favorites-list";
import { activityListDef } from "#app/widgets/definitions/activity-list";
import { teamMembersDef } from "#app/widgets/definitions/team-members";
import { activityHeatmapDef } from "#app/widgets/definitions/activity-heatmap";
import { portfolioMapDef } from "#app/widgets/definitions/portfolio-map";
import { stageThroughputDef } from "#app/widgets/definitions/stage-throughput";
import { noteDef } from "#app/widgets/definitions/note";
import { embedDef } from "#app/widgets/definitions/embed";

/**
 * THE app's single widget registry instance — created here and seeded with every
 * shipped widget as a load-time side effect of importing this module. The kit
 * itself owns no global; this is the one instance the dashboard grid, catalog,
 * and settings surfaces all read, mounted via `<WidgetRegistryProvider>` in the
 * workspace layout.
 *
 * Every widget registers its `definitions/<name>/` folder here plus its type key
 * in the server allowlist (`apps/api/core/src/widgets/widget-types.ts`), in
 * lockstep. Persisted rows whose type has no registered module render the kit's
 * `UnknownWidgetBody` placeholder.
 */
export const widgetRegistry = createWidgetRegistry();

/**
 * THE single source of truth for the dashboard widgets: registration AND the
 * authoring-catalog type set both derive from this one array, so a def can never
 * be registered-but-missing-from-the-Set (which silently hid it from the picker)
 * or vice versa — add it here once (D9). Managed-only defs (`favorites-list`,
 * `team-members`) carry no catalog entries, so the gallery never offers them;
 * they still resolve because they're registered.
 */
 
// distinct config generic; `any` erases it, exactly as the kit registry does.
const DASHBOARD_DEFS: WidgetModule<any>[] = [
  metricDef,
  dimensionBarDef,
  dimensionBatteryDef,
  typeMixDef,
  seriesLineDef,
  stageAgeDef,
  stageMovesDef,
  projectsClosedDef,
  mostActiveUsersDef,
  entityListDef,
  favoritesListDef,
  activityListDef,
  teamMembersDef,
  activityHeatmapDef,
  portfolioMapDef,
  stageThroughputDef,
  noteDef,
  embedDef,
];

// `section-header` is placed via its own gallery affordance, not the Add-Card
// catalog — registered once, plainly, and deliberately absent from the type set.
widgetRegistry.register(sectionHeaderDef);
for (const def of DASHBOARD_DEFS) widgetRegistry.register(def);

/**
 * Which widget types the Add-Card gallery offers. Resolution (rendering a placed
 * widget by `type`) is shared everywhere; only the *catalog* is curated.
 */
export const DASHBOARD_WIDGET_TYPES = new Set<string>(
  DASHBOARD_DEFS.map((def) => def.type),
);
